import { collection, doc, getDoc, getDocs } from "firebase/firestore"
import { Button } from "@mui/material"
import { DateTimeField } from "@mui/x-date-pickers"
import dayjs from "dayjs"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { PopupService } from "react-ui-components/components/PopupService"
import { useNavigate, useSearchParams } from "react-router-dom"
import { deleteAllContent } from "../firebaseHelpers"
import { GenericListServiceConfig } from "../types"
import { AddEditComponent } from "../components/AddEditForm"
import { db } from "../../firebase"

export type UseGenericListStateResult = {
  list: any[]
  loading: boolean
  columns: any[]
  options: Record<string, any>
  reload: () => Promise<void>
  openAdd: (data?: any, formatOverride?: (data: any) => any, onAdded?: () => void) => void
  openEdit: (rowData: any) => void
}

export const useGenericListState = (
  Service: GenericListServiceConfig,
  mainId?: string,
  format?: (data: any) => any,
): UseGenericListStateResult => {
  const [list, setList] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()

  const getListLocation = Service.getListLocation
  const title = Service.TITLE

  const reload = useCallback(async () => {
    try {
      setLoading(true)
      const collectionInfo = getListLocation(mainId)
      let nextList: any[] = []

      if (Array.isArray(collectionInfo)) {
        const querySnapshot = await getDocs(collection(db, collectionInfo.join("/")))
        nextList = querySnapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
      } else {
        const docRef = doc(db, collectionInfo.path)
        const docSnap = await getDoc(docRef)
        if (docSnap.exists()) {
          const rawData: any = { ...docSnap.data(), id: docSnap.id }
          nextList = rawData[collectionInfo.propHoldingSubCollection] || []
        }
      }

      setList(Service.prepareListData ? Service.prepareListData(nextList) : nextList)
    } catch (error: any) {
      console.error(`Get ${title} list error`, error)
      PopupService.pushPopup(error.message || "Unable to load list")
    } finally {
      setLoading(false)
    }
  }, [Service.prepareListData, getListLocation, mainId, title])

  useEffect(() => {
    reload()
  }, [reload])

  const openAddEditPopup = useCallback(
    (data: any, style?: any, formatOverride?: (data: any) => any, onAdded?: () => void) => {
      PopupService.setPopup(
        <AddEditComponent
          Service={Service}
          mainId={mainId}
          format={formatOverride || format}
          data={data}
          onAdded={() => {
            reload()
            PopupService.setPopup(null)
            onAdded && onAdded()
          }}
        />,
        "",
        style,
      )
    },
    [Service, mainId, format, reload],
  )

  const openAdd = useCallback(
    (data?: any, formatOverride?: (data: any) => any, onAdded?: () => void) => {
      openAddEditPopup(data || {}, { width: "90vw", height: "100vh" }, formatOverride, onAdded)
    },
    [openAddEditPopup],
  )

  const openEdit = useCallback(
    (rowData: any) => {
      openAddEditPopup(Service.prepareFormData ? Service.prepareFormData(rowData) : rowData)
    },
    [openAddEditPopup, Service],
  )

  const handledDeepLinkRef = useRef<string | null>(null)

  useEffect(() => {
    const action = searchParams.get("action")
    const id = searchParams.get("id")
    const key = `${action || ""}:${id || ""}`

    if (!action || handledDeepLinkRef.current === key) {
      return
    }
    handledDeepLinkRef.current = key

    if (action === "add") {
      openAdd()
    } else if (action === "edit" && id) {
      openEdit({ id })
    }

    setSearchParams(
      (params) => {
        params.delete("action")
        params.delete("id")
        return params
      },
      { replace: true },
    )
  }, [searchParams, setSearchParams, openAdd, openEdit])

  const columns = useMemo(() => {
    const base = Service.columnsProperties.map((column) => {
      if (column.uiType === "datetime2") {
        return {
          ...column,
          options: {
            customBodyRender: (
              value: string | number | Date | dayjs.Dayjs | null | undefined,
            ) => (
              <DateTimeField
                readOnly
                variant="standard"
                fullWidth
                style={{ width: 220 }}
                defaultValue={dayjs(value)}
                format="LLL"
              />
            ),
          },
        }
      }
      return column
    })

    return [
      ...base,
      {
        name: "edit",
        label: "Edit",
        options: {
          customBodyRenderLite: (dataIndex: number) => {
            const rowData = list[dataIndex]
            return (
              <Button
                variant="contained"
                color="primary"
                onClick={(event) => {
                  event.stopPropagation()
                  openEdit(rowData)
                }}
              >
                Edit
              </Button>
            )
          },
        },
      },
    ]
  }, [Service, list, openEdit])

  const options = useMemo(() => {
    return {
      ...(Service.options || {}),
      onRowsDelete: (params: { data: { dataIndex: number }[] }) => {
        PopupService.setConfirm(
          "Are you sure you want to delete the selected items and all it's data",
          (async () => {
            const ids = params.data
              .map((item) => list[item.dataIndex]?.id)
              .filter(Boolean)
            await deleteAllContent(ids, mainId, Service)
            reload()
          }) as any,
        )
        return false
      },
      onRowClick: Service.onRowClick?.(list, navigate),
    }
  }, [Service, list, navigate, mainId, reload])

  return { list, loading, columns, options, reload, openAdd, openEdit }
}
